import AccountTreeIcon from "@mui/icons-material/AccountTree";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Chip from "@mui/material/Chip";
import LockOutlineIcon from "@mui/icons-material/LockOutline";
import OpenInBrowserIcon from "@mui/icons-material/OpenInBrowser";
import React from "react";
import Tooltip from "@mui/material/Tooltip";
import { SalesOrderStatusD365 } from "../hooks/SalesOrder/D365/useGetSalesOrderHeaderStatusD365";
import {
    createAxiosClient,
    getResolvedRedirectBaseUrl,
} from "@ntg/component-library/utilities";
import { getLineStatusChipStyle } from "../explorer/nodes/lineStatusColors";
import { useLocation, useNavigate } from "react-router";
import { useTheme } from "@mui/material/styles";

interface IServiceOrderToolbarProps {
    D365Status?: SalesOrderStatusD365;
    ServiceOrderID: number;
}

const postCommitServiceOrder = async (
    serviceOrderID: number,
    forInvoice: boolean,
): Promise<string> => {
    const axiosAuth = createAxiosClient("D365FO");
    const response = await axiosAuth.post<string>(
        "/FunctionAppsStarter/CommitServiceOrder",
        { serviceOrderID, forInvoice },
    );
    return response.data;
};

export default function ServiceOrderToolbar(
    props: Readonly<IServiceOrderToolbarProps>,
) {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const openCSServiceOrder = () => {
        const baseUrl = getResolvedRedirectBaseUrl();
        window.open(
            `${baseUrl}Service/ServiceOrder/${props.ServiceOrderID}`,
            "_blank",
            "noopener,noreferrer",
        );
    };

    const handleCommit = async (forInvoice: boolean) => {
        try {
            const instanceId = await postCommitServiceOrder(
                props.ServiceOrderID,
                forInvoice,
            );
            navigate(`${location.pathname}/commit/${instanceId}`);
        } catch (error) {
            console.error(error);
            alert("Failed to start workflow");
        }
    };

    const status = props.D365Status ?? "Loading";
    const isLoading = status === "Loading";
    const isInvoiced = status === "Invoiced";
    const isLocked = isLoading || isInvoiced;
    const hasBeenCommitted = status !== "None" && !isLoading;

    return (
        <div
            style={{
                padding: "9px 3px 1px 3px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "10px",
            }}
        >
            <Chip
                label={status}
                size="small"
                sx={{
                    fontWeight: 600,
                    ...getLineStatusChipStyle(status, isDark),
                }}
            />

            <ButtonGroup
                variant="contained"
                size="small"
                color="primary"
                aria-label="Service order actions"
                sx={{ marginLeft: "auto" }}
            >
                <Tooltip
                    title={
                        isInvoiced
                            ? "Cannot modify once Service Order is Invoiced"
                            : ""
                    }
                >
                    <span>
                        <Button
                            onClick={() => handleCommit(false)}
                            disabled={isLocked}
                            endIcon={isLocked ? <LockOutlineIcon /> : undefined}
                        >
                            {hasBeenCommitted ? "Update" : "Confirm"}
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip
                    title={
                        isInvoiced
                            ? "Cannot submit for invoicing once Service Order is Invoiced"
                            : ""
                    }
                >
                    <span>
                        <Button
                            onClick={() => handleCommit(true)}
                            disabled={isLocked}
                            endIcon={isLocked ? <LockOutlineIcon /> : undefined}
                        >
                            Confirm for Invoice
                        </Button>
                    </span>
                </Tooltip>
            </ButtonGroup>

            <ButtonGroup
                variant="contained"
                size="small"
                color="info"
                aria-label="Explorer button group"
            >
                <Button
                    onClick={() => {
                        navigate(`/explorer/ServiceOrder/${props.ServiceOrderID}`);
                    }}
                    endIcon={<AccountTreeIcon />}
                >
                    Open in Explorer
                </Button>
            </ButtonGroup>

            <ButtonGroup
                variant="contained"
                size="small"
                color="warning"
                aria-label="CS links"
            >
                <Button
                    onClick={openCSServiceOrder}
                    endIcon={
                        <OpenInBrowserIcon htmlColor="inherit !important" />
                    }
                >
                    CS Service Order
                </Button>
            </ButtonGroup>
        </div>
    );
}
